"use client";

import { Card, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React from "react";
import { useParams } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetImportationDetails from "@/hooks/requests/importation-details/use-get-importation-details";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import IImportationDetails from "@/interfaces/importation-details";
import { ImportationDetailsMessages } from "@/messages/importationDetails/importation-details.messages";
import { IFilter } from "@/types/filter.types";
import { BreadCrumb } from "@/types/page.types";

import FiltersForm from "./components/FiltersForm";
import { ImportationDetailsTable } from "./components/Table";
import { getInitialFilterValues } from "./constants";

type Props = {
  messages: ImportationDetailsMessages;
};

export default function ImportationDetailsPage({ messages }: Props) {
  const { importProcessId } = useParams<{ importProcessId: string }>();
  const { lang } = useLanguage();

  const pagination = usePagination();
  const { pageNumber, pageSize } = pagination;
  const initialValue = getInitialFilterValues(importProcessId);
  const filter = useFilters<IImportationDetails>({ initialValue });
  const filters = filter.filters;
  const { data, error, isLoading } = useGetImportationDetails({ filters, pageNumber, pageSize });

  const shouldLoadTable = !!data && data.items.length > 0 && !isLoading;
  const shouldLoadError = !data && !!error && !isLoading;
  const shouldLoadNoData = !!data && data.items.length === 0 && !error && !isLoading;

  if (!importProcessId) return <CircularProgress size={50} />;

  const handleChangeFilters = (newFilters: IFilter<IImportationDetails>[]) => {
    filter.changeFilters(newFilters);
  };

  const BREAD_CRUMBS: BreadCrumb[] = [
    { title: messages.breadCrumbTitle, href: ROUTE_URLS.importations(lang) },
  ];

  return (
    <PageLayout
      messages={messages}
      breadCrumbs={BREAD_CRUMBS}
      title={messages.importationDetailsPageTitle}
    >
      <PageContainer>
        <Formik initialValues={filters} validateOnChange={false} onSubmit={handleChangeFilters}>
          <FiltersForm messages={messages} importProcessId={importProcessId} />
        </Formik>

        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(16) })}>
          <Suspense isSuspended={isLoading} fallback={<CircularProgress size={50} />}>
            <MDBox p={2}>
              <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6">{messages.importationDetailsPageTitle}</MDTypography>
              </MDBox>

              {shouldLoadTable && (
                <ImportationDetailsTable pagination={pagination} data={data} messages={messages} />
              )}

              {shouldLoadError && (
                <Card sx={{ mt: 2 }}>
                  <AlertBlock
                    title={messages.importationDetailsErrorToLoad}
                    description={error.message}
                    alt={messages.importationDetailsNotFoundAlt}
                  />
                </Card>
              )}

              {shouldLoadNoData && (
                <Card sx={{ mt: 2 }}>
                  <AlertBlock
                    title={messages.importationDetailsNotFoundError}
                    description={messages.importationDetailsNotFoundDescription}
                    alt={messages.importationDetailsNotFoundAlt}
                  />
                </Card>
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}
